/**
 * VisibilityCheckPage — self-service tool for dealers / sub-dealers /
 * customers who think a compressor should appear in their lists but
 * doesn't. Paste a serial number → get a plain-English explanation of
 * why it is / isn't visible + actionable next steps.
 *
 * Backend: GET /api/compressors/visibility-check/{serial}
 */
import { useEffect, useState } from "react";
import { Eye, MagnifyingGlass, CheckCircle, Warning, XCircle, ArrowsClockwise, Users, Buildings, ShieldCheck, Lightbulb, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import PinGeneratorDialog from "@/components/PinGeneratorDialog";
import { useT } from "@/lib/i18n";

const VERDICT_TONE = {
  visible: { tone: "emerald", icon: CheckCircle, key: "viscan.verdict_visible", fallback: "Visible to you" },
  staff_unrestricted: { tone: "emerald", icon: ShieldCheck, key: "viscan.verdict_staff", fallback: "Visible — you are staff" },
  out_of_scope: { tone: "rose", icon: XCircle, key: "viscan.verdict_out_of_scope", fallback: "Not in your scope" },
  not_found: { tone: "amber", icon: Warning, key: "viscan.verdict_not_found", fallback: "Not found in our database" },
};

const TONE_CLASS = {
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
  rose: "bg-rose-50 border-rose-300 text-rose-900",
  amber: "bg-amber-50 border-amber-300 text-amber-900",
  blue: "bg-blue-50 border-blue-200 text-blue-900",
};

export default function VisibilityCheckPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const [needle, setNeedle] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pinFor, setPinFor] = useState(null);

  async function run() {
    const sn = needle.trim();
    if (!sn) { toast.error(t("viscan.err_no_serial", "Please paste a serial number first.")); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.get(`/compressors/visibility-check/${encodeURIComponent(sn)}`);
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("viscan.err_lookup", "Lookup failed."));
    } finally { setLoading(false); }
  }

  // Auto-run when the page is opened with ?sn=ABC in the URL (lets us
  // link to it from emails / SMS).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sn = sp.get("sn");
    if (sn) { setNeedle(sn); }
  }, []);
  useEffect(() => {
    if (needle && !result && !loading) {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("sn") === needle) run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle]);

  const verdict = result?.verdict;
  const v = verdict ? VERDICT_TONE[verdict] : null;
  const VIcon = v?.icon || null;

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="visibility-check-page">
      <AdminPageHeader sticky maxWidth="max-w-3xl" testId="visibility-check-back"
        icon={<Eye size={20} weight="fill" className="text-blue-700" />}
        title={t("viscan.page_title", "Why can I (not) see this compressor?")}
        subtitle={t("viscan.page_subtitle", "Paste a serial number and we'll tell you exactly why it does (or doesn't) appear in your lists.")}
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />

      <main className="max-w-3xl mx-auto p-6 pb-32 space-y-5">
        {/* Search bar */}
        <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              {t("viscan.input_label", "Compressor serial number")}
            </span>
            <div className="flex gap-1">
              <input value={needle}
                     onChange={(e) => setNeedle(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && run()}
                     placeholder={t("viscan.input_placeholder", "e.g. 90710207 or c6f51376")}
                     data-testid="visibility-check-input"
                     autoFocus
                     className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none font-mono" />
              <button onClick={run} disabled={loading || !needle.trim()}
                      data-testid="visibility-check-submit"
                      className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
                {loading
                  ? <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                  : <MagnifyingGlass size={12} weight="bold" />}
                {t("viscan.btn_check", "Check")}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {t("viscan.tip", "Tip: use the exact nameplate serial number, or the short internal id from the URL.")}
            </p>
          </label>
        </section>

        {/* Loading / Empty state */}
        {loading && (
          <div className="py-8 text-center" data-testid="visibility-check-loading">
            <ArrowsClockwise size={22} weight="bold" className="mx-auto animate-spin text-slate-400" />
          </div>
        )}

        {!loading && !result && (
          <div className="bg-white border border-slate-200 rounded-md p-4 text-[12px] text-slate-600">
            <Lightbulb size={14} weight="fill" className="inline mr-1 text-amber-500" />
            <strong>{t("viscan.how_it_works_title", "How it works.")}</strong>{" "}
            {t("viscan.how_it_works_body", "The portal restricts every list (Products Sold, PM Service, Commissioning, Warranty claims) to the compressors in your dealer scope. Your scope is built from your own company plus every company below it in the Handling Company tree. A compressor is visible if its Owner_Company, ServiceCompany, or any entry in its comma-separated ResellerID chain matches one company in your scope.")}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3" data-testid="visibility-check-result">
            {/* Verdict banner */}
            <div className={`rounded-md border-2 p-3 ${TONE_CLASS[v.tone]}`}
                 data-testid={`visibility-check-verdict-${verdict}`}>
              <div className="flex items-center gap-2 mb-1">
                <VIcon size={20} weight="fill" />
                <span className="text-sm font-bold tracking-tight">{t(v.key, v.fallback)}</span>
              </div>
              {result.found && (
                <div className="text-[12px] mt-1 opacity-80">
                  <strong>{t("viscan.field_sn", "Serial number")}:</strong> {result.compressor?.serial_number || result.needle}
                  {result.compressor?.model && <> · {result.compressor.model}</>}
                </div>
              )}
            </div>

            {/* Compressor record (only when found) */}
            {result.found && (
              <section className="bg-white border border-slate-200 rounded-md p-4">
                <div className="flex items-start gap-2 mb-2">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 flex-1">
                    {t("viscan.record_title", "Compressor record")}
                  </h3>
                  {/* PIN generator — surfaced only when the compressor
                      is PIN-locked (pin_type > 0) AND the caller can
                      actually see it (privacy: never expose PIN
                      generation for out-of-scope units). */}
                  {result.visible_to_you
                   && Number(result.compressor?.pin_type) > 0
                   && result.compressor?.serial_number && (
                    <button
                      type="button"
                      data-testid="visibility-check-pin-btn"
                      onClick={() => setPinFor({
                        serial_number: result.compressor.serial_number,
                        product_name: result.compressor.model,
                        pin_type: Number(result.compressor.pin_type),
                      })}
                      title={t("customer.pin_title",
                               "Generate response PIN for this compressor (type {{n}})",
                               { n: Number(result.compressor.pin_type) })}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase font-bold tracking-wider rounded-sm text-amber-700 hover:bg-amber-50 border border-amber-300">
                      <Key size={11} weight="bold" />
                      {t("customer.pin_button", "PIN")}
                    </button>
                  )}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
                  <Item label={t("viscan.field_sn", "Serial number")} value={result.compressor.serial_number || "—"} />
                  <Item label={t("viscan.field_model", "Model")} value={result.compressor.model || "—"} />
                  <Item label={t("viscan.field_owner", "Owner company")} value={result.compressor.owner_company_label || "—"} />
                  <Item label={t("viscan.field_service", "Service company")} value={result.compressor.service_company_label || "—"} />
                  <div className="sm:col-span-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {t("viscan.field_reseller_chain", "Reseller chain")}
                    </div>
                    {(result.compressor.reseller_chain || []).length === 0
                      ? <div className="text-[12px] text-slate-500 italic">{t("viscan.reseller_chain_none", "(none set)")}</div>
                      : <ul className="flex flex-wrap gap-1 mt-1">
                          {result.compressor.reseller_chain.map((c, i) => (
                            <li key={i} className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${c.in_your_scope ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                              {c.label}{c.in_your_scope && " ✓"}
                            </li>
                          ))}
                        </ul>}
                  </div>
                </dl>
              </section>
            )}

            {/* Reasons */}
            <section className="bg-white border border-slate-200 rounded-md p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1">
                {result.visible_to_you ? <CheckCircle size={11} weight="fill" className="text-emerald-600" /> : <Warning size={11} weight="fill" className="text-amber-600" />}
                {result.visible_to_you ? t("viscan.why_visible", "Why it's visible") : t("viscan.why_not_visible", "Why it isn't")}
              </h3>
              <ul className="space-y-1.5">
                {(result.reasons || []).map((r, i) => (
                  <li key={i} className="text-[12px] text-slate-800"
                      data-testid={`visibility-check-reason-${i}`}>
                    <Markdownish text={r} />
                  </li>
                ))}
              </ul>
            </section>

            {/* Next steps */}
            <section className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-blue-900 mb-2 flex items-center gap-1">
                <Lightbulb size={11} weight="fill" /> {t("viscan.next_steps_title", "What to do next")}
              </h3>
              <ul className="space-y-1.5">
                {(result.next_steps || []).map((s, i) => (
                  <li key={i} className="text-[12px] text-blue-900"
                      data-testid={`visibility-check-step-${i}`}>
                    <Markdownish text={s} />
                  </li>
                ))}
              </ul>
            </section>

            {/* Your scope summary (non-staff only) */}
            {result.your_scope && !result.your_scope.is_staff && (
              <section className="bg-white border border-slate-200 rounded-md p-4">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1">
                  <Buildings size={11} weight="fill" /> {t(
                    result.your_scope.size === 1 ? "viscan.your_network_one" : "viscan.your_network_many",
                    result.your_scope.size === 1 ? "Your network ({n} company)" : "Your network ({n} companies)",
                    { n: result.your_scope.size }
                  )}
                </h3>
                {result.your_scope.sample_company_names?.length > 0 ? (
                  <ul className="flex flex-wrap gap-1">
                    {result.your_scope.sample_company_names.map((n) => (
                      <li key={n} className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-700">
                        {n}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[12px] text-slate-500 italic">
                    {t("viscan.unlinked_hint", "You aren't linked to any customer record in our database yet. Ask your ACC sales contact to add you via Persons_for_Customer.")}
                  </p>
                )}
              </section>
            )}
          </div>
        )}
      </main>
      {pinFor && (
        <PinGeneratorDialog
          serialNumber={pinFor.serial_number}
          productName={pinFor.product_name}
          pinType={pinFor.pin_type}
          onClose={() => setPinFor(null)}
        />
      )}
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[12px] text-slate-800 break-words">{value}</div>
    </div>
  );
}

/** Tiny inline **bold** renderer so we can mark up reasons without a real Markdown lib. */
function Markdownish({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>
      )}
    </>
  );
}
